import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import "common/assets/css/App.css";
import {
  HashRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import AuthLayout from "./layouts/auth";
import { ChakraProvider } from "@chakra-ui/react";
import theme from "common/theme/theme";
import { ThemeEditorProvider } from "@hypertheme-editor/chakra-ui";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Provider } from "react-redux";
import store from "./common/redux/store";
import { msalInstance } from "./common/auth-config";
import qs from "qs";
import { MsalProvider } from "@azure/msal-react";
import { SSOProvider } from "common/contexts/SSOContext";
import "./i18n";
import { SSORole } from "common/contexts/SSOContext";
import { saveDataToStorage } from "common/utils/storage";
import { constant } from "common/constant";
import axios from "axios";
import { postApi } from "common/services/api";
import UnifiedDashboard from "layouts/UnifiedLayout";
import { jwtDecode } from "jwt-decode";

const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser && storedUser !== "undefined") {
      return JSON.parse(storedUser);
    }
  } catch (err) {
    console.error("Error parsing user from localStorage:", err);
  }
  return null;
};

function App() {
  const oauthHandledRef = useRef(false);
  const urlParams = new URLSearchParams(window.location.search);
  const codeValue = urlParams.get("code");
  const { codeVerifier } = SSORole();
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState(() => ({
    token: localStorage.getItem("token") || sessionStorage.getItem("token"),
    user: getStoredUser(),
    ready: false,
  }));
  const token = authState.token;
  const user = authState.user;

  useEffect(() => {
    let isMounted = true;

    const bootstrapAuth = async () => {
      const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");
      const storedRefreshToken =
        localStorage.getItem(constant.refreshToken) || sessionStorage.getItem(constant.refreshToken);
      const storedUser = getStoredUser();

      if (!storedToken) {
        if (isMounted) {
          setAuthState({ token: null, user: storedUser, ready: true });
        }
        return;
      }

      let shouldRefresh = false;
      try {
        const decodedToken = jwtDecode(storedToken);
        const currentTime = Date.now() / 1000;
        shouldRefresh = !decodedToken?.exp || decodedToken.exp <= currentTime;
      } catch (error) {
        shouldRefresh = true;
      }

      if (!shouldRefresh) {
        if (isMounted) {
          setAuthState({ token: storedToken, user: storedUser, ready: true });
        }
        return;
      }

      if (!storedRefreshToken) {
        if (isMounted) {
          setAuthState({ token: null, user: null, ready: true });
        }
        return;
      }

      try {
        const storage = localStorage.getItem("token") || localStorage.getItem(constant.refreshToken)
          ? localStorage
          : sessionStorage;
        const response = await axios.post(`${constant.baseUrl}api/user/refresh-token`, {
          refreshToken: storedRefreshToken,
        });

        const nextToken = response?.data?.token || null;
        const nextRefreshToken = response?.data?.refreshToken || null;
        const nextUser = response?.data?.user || storedUser;

        if (nextToken) {
          storage.setItem("token", nextToken);
        }
        if (nextRefreshToken) {
          storage.setItem(constant.refreshToken, nextRefreshToken);
        }
        if (nextUser) {
          localStorage.setItem("user", JSON.stringify(nextUser));
        }

        if (isMounted) {
          setAuthState({ token: nextToken, user: nextUser, ready: true });
        }
      } catch (error) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        localStorage.removeItem(constant.refreshToken);
        sessionStorage.removeItem(constant.refreshToken);
        localStorage.removeItem("user");
        if (isMounted) {
          setAuthState({ token: null, user: null, ready: true });
        }
      }
    };

    bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!authState.ready) return;

    const nextToken = localStorage.getItem("token") || sessionStorage.getItem("token");
    const nextUser = getStoredUser();

    if (nextToken !== authState.token || JSON.stringify(nextUser) !== JSON.stringify(authState.user)) {
      setAuthState({
        token: nextToken,
        user: nextUser,
        ready: true,
      });
    }
  }, [location.pathname, authState.ready, authState.token, authState.user]);
  // Handle OAuth code
  useEffect(() => {
    if (codeValue) saveDataToStorage(constant.authCode, codeValue);
  }, [codeValue]);

  // Handle OAuth authentication
  useEffect(() => {
    if (codeValue && oauthHandledRef.current) return;
    async function authHandle() {
      async function authCode() {
        oauthHandledRef.current = true;
        const data = qs.stringify({
          grant_type: constant.authorizationCode,
          client_id: constant.clientID,
          code: codeValue,
          redirect_uri: constant.redirectPath,
          scope: constant.scope,
          code_verifier: localStorage.getItem("code-verifier"),
        });

        const config = {
          method: "post",
          maxBodyLength: Infinity,
          url: "https://login.microsoftonline.com/6a05b661-c8a0-4bdd-b098-7314d26fede1/oauth2/v2.0/token",
          headers: {
            Accept: "*/*",
            "Accept-Language": "en-US,en;q=0.9,hi;q=0.8",
          },
          data: data,
        };

        try {
          const response = await axios.request(config);

          if (response.data) {
            try {
              await postApi("api/user/outlook/sync", {
                accessToken: response.data.access_token,
                refreshToken: response.data.refresh_token,
                scope: response.data.scope,
                tokenType: response.data.token_type,
                expiresIn: response.data.expires_in,
              });
              localStorage.setItem("isOutlookAccountSynced", JSON.stringify(true));
              localStorage.setItem("isOutlookAccount", JSON.stringify(true));
              // Security: never persist Outlook tokens in browser storage.
              localStorage.removeItem(constant.refreshToken);
              localStorage.removeItem(constant.outlookAccessToken);
            } catch (syncError) {
              console.error("Outlook sync API failed:", syncError);
            }
          }

          // Clean up URL and redirect
          window.history.replaceState({}, document.title, "/");
          const connectSource = localStorage.getItem("outlook_connect_source");
          const redirectUserId = localStorage.getItem("outlook_connect_user_id");
          const redirectUrl = localStorage.getItem("outlook_connect_return_url");
          const connectMode = localStorage.getItem("outlook_connect_mode");
          localStorage.removeItem("outlook_connect_source");
          localStorage.removeItem("outlook_connect_user_id");
          localStorage.removeItem("outlook_connect_return_url");
          localStorage.removeItem("outlook_connect_mode");

          if (connectSource === "settings_email") {
            const fallbackUser = (() => {
              try {
                return JSON.parse(localStorage.getItem("user") || "{}");
              } catch (e) {
                return {};
              }
            })();
            const finalUserId = redirectUserId || fallbackUser?._id;

            if (finalUserId) {
              localStorage.setItem("currentPageTabIndex", "2");
              localStorage.setItem("currentPageUrl", `/userView/${finalUserId}`);
            }

            if (connectMode === "popup" && window.opener && !window.opener.closed) {
              window.opener.postMessage(
                { type: "outlook-auth-success" },
                window.location.origin
              );
              window.close();
              return;
            }
            const targetHashPath = redirectUrl || (
              finalUserId
                ? `/userView/${finalUserId}/email?outlookConnected=1`
                : "/dashboard"
            );
            window.location.hash = targetHashPath;
            return;
          } else {
            navigate(getDefaultRoute(user), { replace: true });
          }
        } catch (error) {
          console.error("OAuth error:", error);
          navigate("/auth/sign-in", { replace: true });
        }
      }

      if (codeValue) {
        await authCode();
      }
    }

    authHandle();
  }, [codeValue, navigate, user]);

  // Helper function to get the actual role name
  const getUserRoleName = (user) => {
    if (!user) return null;

    // Check if activeRoleId is populated (has roleName property)
    let role = user?.activeRoleId?.roleName;

    // If activeRoleId is just an ID string, look it up in roles array
    if (!role && user?.activeRoleId && user?.roles) {
      const activeRoleId = typeof user.activeRoleId === 'string'
        ? user.activeRoleId
        : user.activeRoleId._id || user.activeRoleId;
      const activeRole = user.roles.find((r) => {
        const roleId = typeof r === 'object' ? (r._id || r) : r;
        return roleId?.toString() === activeRoleId?.toString();
      });
      role = activeRole?.roleName;
    }

    // Fallback to user.role if still not found
    return role || user?.role;
  };

  const getDefaultRoute = (user) => {
    if (!user || !user.role) return "/auth/sign-in";
    // Redirect reviewers to document verification instead of dashboard
    const activeRole = getUserRoleName(user);
    if (activeRole === "reviewer") {
      return "/recruitment/document-verification";
    }
    return "/dashboard";
  };

  useEffect(() => {
    if (codeValue) return;
    if (token && user?.role) {
      const activeRole = getUserRoleName(user);
      const isReviewer = activeRole === "reviewer";

      // Redirect authenticated users away from auth pages
      if (location.pathname.startsWith("/auth")) {
        navigate(getDefaultRoute(user), { replace: true });
        return;
      }

      // Handle role-specific routing
      if (isReviewer) {
        // For reviewers: if on root or any non-allowed page, redirect to document verification
        const allowedPaths = [
          "/recruitment/document-verification",
          "/recruitment/candidate-create",
          "/recruitment/application-create",
          "/recruitment/onboarding-checklist",
          "/performance/assigned-reviews",
          "/performance/team-performance"
        ];

        const isAllowed = allowedPaths.some(path =>
          location.pathname === path || location.pathname.startsWith(path + "/")
        );

        if (!isAllowed || location.pathname === "/") {
          navigate("/recruitment/document-verification", { replace: true });
          return;
        }
      } else {
        // For non-reviewers: only block document-verification for basic "employee" or "user" roles
        // Other roles (recruiter, hiringManager, superAdmin) should have access
        const isBasicEmployee = activeRole === "employee" || activeRole === "user";

          if (isBasicEmployee) {
          // Allow employees to access Performance routes
          const allowedPaths = [
            "/performance/my-reviews",
            "/performance/team-reviews",
            "/performance/assigned-reviews",
            // Legacy alias for Team Reviews (older URL)
            "/performance/peer-reviews"
          ];

          const isPerformanceRoute = allowedPaths.some(path =>
            location.pathname === path || location.pathname.startsWith(path + "/")
          );

          if (isPerformanceRoute) {
            // Allow access to Performance routes
            return;
          }

          // Employees should not access document-verification, onboarding-checklist, tools, admin, or organization
          const restrictedPaths = [
            "/recruitment/document-verification",
            "/recruitment/onboarding-checklist",
            "/tools",
            "/admin",
            "/organization"
          ];

          const isRestricted = restrictedPaths.some(path =>
            location.pathname === path || location.pathname.startsWith(path + "/")
          );

          if (isRestricted) {
            navigate("/dashboard", { replace: true });
            return;
          }
        }

        // Redirect from root to default route for all non-reviewers
        if (location.pathname === "/") {
          navigate(getDefaultRoute(user), { replace: true });
          return;
        }
      }
    }
  }, [token, user?.role, user?.activeRoleId, user?.roles, location.pathname, navigate]);

  const renderRoutes = () => {
    if (!authState.ready) {
      return <Route path="/*" element={<div>Loading...</div>} />;
    }

    // If no token or user, show auth layout
    if (!token || !user?.role) {
      return <Route path="/*" element={<AuthLayout />} />;
    }

    // Single unified layout for all authenticated users
    return <Route path="/*" element={<UnifiedDashboard />} />;
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {!codeValue && (
        <Routes>
          {/* Public routes - accessible without authentication */}
          <Route
            path="/verify-background/:token"
            element={
              <React.Suspense fallback={<div>Loading...</div>}>
                {React.createElement(React.lazy(() => import("services/Recruit/pages/backgroundVerification/VerificationForm")))}
              </React.Suspense>
            }
          />
          {renderRoutes()}
        </Routes>
      )}
    </>
  );
}

ReactDOM.render(
  <Provider store={store}>
    <ChakraProvider theme={theme}>
      <React.StrictMode>
        <ThemeEditorProvider>
          <Router>
            <SSOProvider>
              <MsalProvider instance={msalInstance}>
                <GoogleOAuthProvider
                  clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
                >
                  <App />
                </GoogleOAuthProvider>
              </MsalProvider>
            </SSOProvider>
          </Router>
        </ThemeEditorProvider>
      </React.StrictMode>
    </ChakraProvider>
  </Provider>,
  document.getElementById("root")
);
